<template>
    <div class="number-input" :class="{ 'is-disabled': disabled }">
        <div v-if="label" class="number-input__label">{{ label }}</div>
        <div class="number-input__controls">
            <button
                type="button"
                class="number-input__button number-input__button--minus"
                @click.prevent="subtract"
                @mousedown="onMouseDown(subtract)"
                @mouseup="onMouseUp"
                @mouseleave="onMouseUp"
            >
                <IconMinus />
            </button>
            <span v-if="!withInput" class="number-input__value">
                {{ displayValue }}
            </span>
            <div v-else class="number-input__input-wrap">
                <span v-if="!editing" class="number-input__value" @click="setEditingMode">
                    {{ displayValue }}
                </span>
                <input
                    v-if="editing"
                    ref="input"
                    v-model.number="local"
                    v-autowidth="{ maxWidth: '90px', minWidth: '50px' }"
                    :class="{ 'is-active': editing }"
                    class="number-input__input"
                    type="text"
                    @keypress="onInput"
                    @keypress.enter="save"
                />
                <button
                    v-if="editing"
                    class="number-input__input-button number-input__input-button--save"
                    type="button"
                    @click.stop="save"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="10"
                        height="10"
                    >
                        <path
                            d="M504.502 75.496c-9.997-9.998-26.205-9.998-36.204 0L161.594 382.203 43.702 264.311c-9.997-9.998-26.205-9.997-36.204 0-9.998 9.997-9.998 26.205 0 36.203l135.994 135.992c9.994 9.997 26.214 9.99 36.204 0L504.502 111.7c9.998-9.997 9.997-26.206 0-36.204z"
                        />
                    </svg>
                </button>
                <button
                    v-if="editing"
                    type="button"
                    class="number-input__input-button"
                    @click.stop="clear"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        width="10"
                        height="10"
                    >
                        <path
                            d="M505.943 6.058c-8.077-8.077-21.172-8.077-29.249 0L6.058 476.693c-8.077 8.077-8.077 21.172 0 29.249A20.612 20.612 0 0020.683 512a20.614 20.614 0 0014.625-6.059L505.943 35.306c8.076-8.076 8.076-21.171 0-29.248z"
                        />
                        <path
                            d="M505.942 476.694L35.306 6.059c-8.076-8.077-21.172-8.077-29.248 0-8.077 8.076-8.077 21.171 0 29.248l470.636 470.636a20.616 20.616 0 0014.625 6.058 20.615 20.615 0 0014.624-6.057c8.075-8.078 8.075-21.173-.001-29.25z"
                        />
                    </svg>
                </button>
            </div>
            <button
                type="button"
                class="number-input__button number-input__button--plus"
                @click.prevent="add"
                @mousedown="onMouseDown(add)"
                @mouseup="onMouseUp"
                @mouseleave="onMouseUp"
            >
                <IconPlus />
            </button>
        </div>
    </div>
</template>

<script>
// @TODO adapt for FormField
import { IconPlus, IconMinus } from '@speechki-frontend/shared/ui/icons';

export default {
    name: 'NumberInput',
    components: {
        IconPlus,
        IconMinus
    },
    emits: ['update:modelValue'],
    props: {
        //eslint-disable-next-line
        modelValue: {
            default: 0
        },
        step: {
            type: [Number, String],
            default: 0.001
        },
        min: {
            type: Number,
            default: null
        },
        max: {
            type: Number,
            default: null
        },
        label: {
            type: String,
            default: ''
        },
        withInput: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editing: false,
            local: 0,
            interval: null,
            delay: null,
            mousedown: false
        };
    },
    computed: {
        stepLength() {
            if (this.step % 1 === 0) return 0;
            return (this.step + '').split('.')[1].length;
        },
        numStep() {
            return Number(this.step);
        },
        displayValue() {
            if (this.modelValue == undefined) return '';

            return Number(this.modelValue).toFixed(this.stepLength);
        }
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(value) {
                if (value == undefined) return;

                this.local = Number(value).toFixed(this.stepLength);
            }
        }
    },
    unmounted() {
        if (this.interval) {
            clearInterval(this.interval);
        }
    },
    methods: {
        add() {
            this.editing = false;
            const value = +(this.modelValue + this.numStep).toFixed(this.stepLength);
            if (this.max !== null && value > this.max) return;
            this.$emit('update:modelValue', value);
        },
        subtract() {
            this.editing = false;
            const value = +(this.modelValue - this.numStep).toFixed(this.stepLength);
            if (this.min !== null && value < this.min) return;
            this.$emit('update:modelValue', value);
        },
        setEditingMode() {
            this.editing = true;
            this.$nextTick(() => {
                this.$refs.input.focus();
            });
        },
        onInput(event) {
            const { keyCode } = event;
            if (keyCode === 8) {
                return true;
            }
            if (keyCode === 46) {
                if (!this.stepLength || this.local.toString().indexOf('.') > -1) {
                    event.preventDefault();
                }
                return;
            }
            if (keyCode > 31 && (keyCode < 48 || keyCode > 57)) {
                event.preventDefault();
            }
        },
        save() {
            if (!this.local) {
                this.$emit('update:modelValue', 0);
                this.editing = false;
                return;
            }
            const value = Number(this.local.toFixed(this.stepLength));
            if (
                (this.min !== null && value < this.min) ||
                (this.max !== null && value > this.max)
            ) {
                this.editing = false;
                return;
            }
            this.$emit('update:modelValue', value);
            this.editing = false;
        },
        clear() {
            this.local = this.modelValue;
            this.editing = false;
        },
        onMouseDown(action) {
            this.mousedown = true;
            this.delay = setTimeout(() => {
                if (this.mousedown) {
                    this.interval = setInterval(() => {
                        action();
                    }, 80);
                }
            }, 200);
        },
        onMouseUp() {
            this.mousedown = false;
            clearInterval(this.interval);
            clearTimeout(this.delay);
            this.interval = null;
            this.delay = null;
        }
    }
};
</script>

<style lang="scss" src="./NumberInput.scss"></style>
